// Ported from the portal's components/v1/Button.tsx: same classes, colours, radius and sizes.
import { Tooltip, type TooltipProps } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import styled from "styled-components";

const ButtonStyled = styled.button`
  text-transform: capitalize;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 11px;
  font-family: var(--st-font-family-primary);
  font-style: normal;
  font-weight: 600;
  outline: none;
  padding: 0.5rem 1.5rem;
  border-radius: 7px;
  border: 0;
  user-select: none;
  text-decoration: none;
  min-height: 44px;
  transition: background 0.15s ease;

  &.st-v1-button-sm { font-size: 14px; min-height: 36px; padding: 0.35rem 1rem; svg { width: 14px; height: 14px; } }
  &.st-v1-button-md { font-size: 16px; svg { width: 16px; height: 16px; } }
  &.st-v1-button-lg { font-size: 18px; min-height: 52px; svg { width: 18px; height: 18px; } }
  &.st-v1-button-xl { font-size: 21px; min-height: 60px; svg { width: 21px; height: 21px; } }

  &.st-v1-button-primary { background: #4c54a0; color: #ffffff; &:not(:disabled):hover { background: #2c368e; } &:focus { box-shadow: 0px 0px 0px 5px #4c54a033; } }
  &.st-v1-button-secondary { background: #f1f1f5; color: #696974; &:not(:disabled):hover { background: #f2f2f7; } &:focus { box-shadow: 0px 0px 0px 5px rgba(76, 84, 160, 0.2); } }
  &.st-v1-button-danger { background: #fc5a5a; color: #ffffff; &:focus { box-shadow: 0px 0px 0px 5px #cfaaaa4e; } }
  &.st-v1-button-success { background: #3dd598; color: #ffffff; &:focus { box-shadow: 0px 0px 0px 5px #cfaaaa4e; } }
  &.st-v1-button-text-primary { background: transparent; color: #4c54a0; border: 0; padding: 5px; &:not(:disabled):hover { background: #edeef5; } }
  &.st-v1-button-text-danger { background: transparent; color: #fc5a5a; border: 0; padding: 5px; &:not(:disabled):hover { background: #edeef5; } }
  &.st-v1-button-text-secondary { background: transparent; color: #92929d; border: 0; padding: 5px; &:not(:disabled):hover { background: #edeef5; } }
  &.st-v1-button-outlined-primary { background: #ffffff; border: 1px solid #4c54a0; color: #4c54a0; &:not(:disabled):hover { background: #f2f2f7; } &:focus { box-shadow: 0px 0px 0px 5px #4c54a033; } }
  &.st-v1-button-outlined-success { background: #ffffff; border: 1px solid #3dd598; color: #3dd598; &:not(:disabled):hover { background: #f2f2f7; } }
  &.st-v1-button-outlined-danger { background: #ffffff; border: 1px solid #fc5a5a; color: #fc5a5a; &:not(:disabled):hover { background: #fff3f3; } }
  &.st-v1-button-outlined-secondary { background: #ffffff; border: 1px solid #d2d2d5; color: #44444f; &:not(:disabled):hover { background: #f2f2f7; } }
  &.st-v1-button-white { background: #ffffff; color: #4c54a0; &:not(:disabled):hover { background: #f2f2f7; } }

  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &.w-100 { width: 100%; }
`;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "name"> {
  name?: ReactNode;
  buttonType?: "filled" | "outlined" | "text" | "white";
  color?: "primary" | "secondary" | "danger" | "success";
  size?: "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  icon?: ReactNode;
  tooltip?: TooltipProps["title"];
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { name, buttonType = "filled", color = "primary", size = "md", loading, icon, tooltip, className = "", children, disabled, ...rest }, ref,
) {
  const variant = buttonType === "filled" ? `st-v1-button-${color}` : buttonType === "white" ? "st-v1-button-white" : `st-v1-button-${buttonType}-${color}`;
  const btn = (
    <ButtonStyled ref={ref} className={`st-v1-button ${variant} st-v1-button-${size} ${className}`.trim()} disabled={disabled || loading} {...rest}>
      {loading ? <LoadingOutlined spin /> : icon}
      {name ?? children}
    </ButtonStyled>
  );
  return tooltip ? <Tooltip title={tooltip}>{btn}</Tooltip> : btn;
});

export default Button;
